import React, { useState } from 'react';
import { Search, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';

const HeroSection = ({ onTrack }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const { toast } = useToast();

  const handleTrack = () => {
    // Redirect to tracking page
    window.location.href = '/track-shipment';
  };

  const handleGetQuote = () => {
    const section = document.getElementById('contact');
    section?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative pt-28 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/50" />
      </div>
      
      {/* Original background decoration kept as fallback */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-900 rounded-full opacity-10 blur-3xl z-0"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-800 rounded-full opacity-10 blur-3xl z-0"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8" data-aos="fade-up" data-aos-duration="1000">
            <div className="space-y-4">
              <div className="inline-block" data-aos="fade-right" data-aos-delay="200">
                <span className="bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  UPTO 75% Off
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight" data-aos="fade-up" data-aos-delay="300">
                Ship Smarter.
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-400"> Ship with Shippzy</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed" data-aos="fade-up" data-aos-delay="400">
                Canada's trusted international courier service. Compare carriers, save up to 75%, ship to 185+ countries worldwide.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="500">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg hover:shadow-red-500/50"
                onClick={handleGetQuote}
              >
                Get a Free Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-8 py-6 text-lg transition-all duration-200 bg-transparent"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Our Services
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div>
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-400">185+</div>
                <div className="text-sm text-gray-400">Countries</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-400">75%</div>
                <div className="text-sm text-gray-400">Savings</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-400">24/7</div>
                <div className="text-sm text-gray-400">Support</div>
              </div>
            </div>
          </div>

          {/* Right Content - Premium Tracking Card */}
          <div 
            className="relative rounded-2xl p-8 space-y-6 overflow-hidden"
            style={{
              background: 'rgba(31, 41, 55, 0.7)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px 0 rgba(220, 38, 38, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            }}
            data-aos="fade-left"
            data-aos-delay="200"
          >
            {/* Gradient glow effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-red-600/30 to-yellow-500/30 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-yellow-500/20 to-red-600/20 rounded-full blur-3xl"></div>
            
            <Tabs defaultValue="track" className="w-full relative z-10">
              <TabsList className="grid w-full grid-cols-2 bg-gray-900/60 backdrop-blur-sm">
                <TabsTrigger 
                  value="track" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-600 data-[state=active]:to-red-500 data-[state=active]:shadow-lg transition-all"
                >
                  Track Shipment
                </TabsTrigger>
                <TabsTrigger 
                  value="quote" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-600 data-[state=active]:to-red-500 data-[state=active]:shadow-lg transition-all"
                >
                  Get Quote
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="track" className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Track Your Package</h3>
                  <p className="text-sm text-gray-300">Enter your tracking number to get real-time updates</p>
                </div>
                <div className="space-y-3">
                  <Input
                    placeholder="Enter tracking number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                    className="h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-400 backdrop-blur-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50 transition-all"
                  />
                  <Button 
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white h-12 transition-all duration-200 shadow-lg hover:shadow-red-500/50"
                    onClick={handleTrack}
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Track Package
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="quote" className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Get Instant Quote</h3>
                  <p className="text-sm text-gray-300">Calculate shipping costs for your package</p>
                </div>
                <div className="space-y-3">
                  <Input 
                    placeholder="From (City/Postal Code)" 
                    className="h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-400 backdrop-blur-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50 transition-all" 
                  />
                  <Input 
                    placeholder="To (City/Postal Code)" 
                    className="h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-400 backdrop-blur-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50 transition-all" 
                  />
                  <Input 
                    placeholder="Weight (kg)" 
                    type="number" 
                    className="h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-400 backdrop-blur-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50 transition-all" 
                  />
                  <Button 
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white h-12 transition-all duration-200 shadow-lg hover:shadow-red-500/50"
                    onClick={() => toast({ title: "Quote Request", description: "Our team will contact you shortly!" })}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Calculate Price
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;